import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';

const TrainingHub = () => {
    const { language } = useLanguage();

    // --- State ---
    const [modules, setModules] = useState([]);
    const [selectedModuleId, setSelectedModuleId] = useState(null);
    const [activeTab, setActiveTab] = useState('exams'); // 'exams' | 'courses' | 'stats'
    const [selectedCategory, setSelectedCategory] = useState('full'); // 'full' | 'law' | 'meteo' | 'nav' | 'gen' | 'all'
    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- Effects ---
    useEffect(() => {
        const loadModules = async () => {
            try {
                const data = await api.getModules();
                setModules(data);
                // Default to PPL if available, else first
                if (data.length > 0 && !selectedModuleId) {
                    const ppl = data.find(m => m.id === 'ppl');
                    setSelectedModuleId(ppl ? ppl.id : data[0].id);
                }
            } catch (error) {
                console.error("Failed to load modules", error);
            } finally {
                setLoading(false);
            }
        };
        loadModules();
    }, []);

    useEffect(() => {
        const loadExams = async () => {
            if (!selectedModuleId) return;
            try {
                const data = await api.getExamsByModule(selectedModuleId);
                setExams(data);
            } catch (error) {
                console.error("Failed to load exams", error);
                setExams([]);
            }
        };
        setLoading(true); // Short loading state for transition
        loadExams().then(() => setLoading(false));
    }, [selectedModuleId]);

    // --- Handlers ---
    const handleModuleSelect = (id) => setSelectedModuleId(id);
    const handleTabChange = (tab) => setActiveTab(tab);
    const handleCategorySelect = (cat) => setSelectedCategory(cat);

    // --- Helpers ---
    const getFilteredExams = () => {
        if (!exams) return [];

        const currentModule = modules.find(m => m.id === selectedModuleId);
        if (currentModule && !currentModule.hasCategories) {
            // For modules without specific sub-categories (like PSTAR), 
            // we show all exams or filter by a default type if needed.
            return exams;
        }

        if (selectedCategory === 'all') return exams;
        return exams.filter(e => e.category === selectedCategory);
    };

    const currentModule = modules.find(m => m.id === selectedModuleId);

    if (loading && !currentModule) {
        return <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading...</div>;
    }

    const filteredExams = getFilteredExams();
    const showCategories = currentModule?.hasCategories;

    // --- Render Helpers ---

    const CategoryCard = ({ id, label, icon, active }) => (
        <button
            onClick={() => handleCategorySelect(id)}
            className={`flex flex-col items-center justify-center p-6 rounded-2xl border transition-all duration-300 w-full aspect-video md:aspect-auto md:h-32
                ${active
                    ? 'bg-gray-800 border-white/20 shadow-lg shadow-black/40 ring-1 ring-white/10 dark:bg-gray-800 dark:border-white/20'
                    : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 dark:bg-gray-900/50 dark:border-gray-800 dark:hover:bg-gray-800 dark:hover:border-gray-700'
                }
            `}
        >
            <div className={`mb-3 ${active ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                {icon}
            </div>
            <span className={`text-sm font-medium ${active ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                {label}
            </span>
        </button>
    );

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-black dark:text-white p-4 md:p-8 font-sans transition-colors duration-300">

            {/* Header / Module Selector */}
            <header className="mb-8 flex items-center justify-between">
                <div className="flex flex-wrap gap-2">
                    {modules.map((m) => (
                        <button
                            key={m.id}
                            onClick={() => handleModuleSelect(m.id)}
                            style={{
                                backgroundColor: selectedModuleId === m.id ? m.newBackgroundColor : undefined
                            }}
                            className={`px-4 py-2 rounded-full text-sm font-bold transition-colors
                                ${selectedModuleId === m.id
                                    ? 'text-white shadow-lg'
                                    : 'bg-white text-gray-500 hover:bg-gray-100 dark:bg-gray-900/80 dark:text-gray-400 dark:hover:bg-gray-800'
                                }
                            `}
                        >
                            {m.title}
                        </button>
                    ))}
                    <button className="w-9 h-9 rounded-full bg-white dark:bg-gray-900 text-gray-400 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800 transition shadow-sm">
                        +
                    </button>
                </div>
            </header>

            {/* Main Navigation Tabs */}
            <div className="flex items-center gap-6 mb-8 border-b border-gray-200 dark:border-gray-800 pb-1">
                {['exams', ...(currentModule?.hasCourses ? ['courses'] : []), 'stats'].map((tab) => (
                    <button
                        key={tab}
                        onClick={() => handleTabChange(tab)}
                        className={`text-lg font-bold pb-2 relative transition-colors ${activeTab === tab
                            ? 'text-gray-900 dark:text-white'
                            : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                            }`}
                    >
                        {tab === 'exams' && (language === 'fr' ? 'Examens' : 'Exams')}
                        {tab === 'courses' && (language === 'fr' ? 'Cours' : 'Courses')}
                        {tab === 'stats' && (language === 'fr' ? 'Statistiques' : 'Statistics')}

                        {/* Active Indicator */}
                        {activeTab === tab && (
                            <div className="absolute bottom-0 left-0 w-full h-[2px] bg-red-500 dark:bg-white rounded-t-full" />
                        )}
                    </button>
                ))}
            </div>

            {/* Tab: Examens */}
            {activeTab === 'exams' && (
                <div className="space-y-8 fade-in">

                    {/* "Mots-clés" Hero Card */}
                    <div className="bg-white dark:bg-gradient-to-r dark:from-gray-900 dark:to-gray-800 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between border border-gray-100 dark:border-gray-800 shadow-xl dark:shadow-none">
                        <div className="flex items-center gap-4 mb-4 md:mb-0">
                            <div className="w-16 h-16 rounded-xl bg-green-100 text-green-600 dark:bg-green-400 dark:text-black flex items-center justify-center">
                                {/* Simple Icon placeholder */}
                                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                    {language === 'fr' ? 'Mots-clés' : 'Keywords'}
                                </h3>
                                <p className="text-gray-500 dark:text-gray-400 text-sm max-w-sm">
                                    {language === 'fr'
                                        ? 'Un examen basé uniquement sur vos mots-clés'
                                        : 'An exam based solely on your keywords'
                                    }
                                </p>
                            </div>
                        </div>
                        <button className="px-6 py-2 bg-[#4c6ef5] hover:bg-[#405ec9] text-white rounded-full font-semibold transition shadow-lg shadow-blue-500/20">
                            {language === 'fr' ? 'Commencer' : 'Start'}
                        </button>
                    </div>

                    {/* Category Selection - Horizontal slider on mobile, grid on desktop */}
                    {showCategories && (
                        <div className="-mx-4 md:mx-0">
                            <div className="flex md:grid md:grid-cols-5 gap-3 md:gap-4 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none no-scrollbar px-4 md:px-0 pb-2 md:pb-0">
                                <div className="flex-shrink-0 md:flex-shrink snap-center w-[140px] md:w-auto">
                                    <CategoryCard
                                        id="law"
                                        active={selectedCategory === 'law'}
                                        label={language === 'fr' ? 'Droit aérien' : 'Air Law'}
                                        icon={
                                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                                            </svg>
                                        }
                                    />
                                </div>
                                <div className="flex-shrink-0 md:flex-shrink snap-center w-[140px] md:w-auto">
                                    <CategoryCard
                                        id="gen"
                                        active={selectedCategory === 'gen'}
                                        label={language === 'fr' ? 'Connaissance G' : 'Gen. Knowledge'}
                                        icon={
                                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                            </svg>
                                        }
                                    />
                                </div>
                                <div className="flex-shrink-0 md:flex-shrink snap-center w-[140px] md:w-auto">
                                    <CategoryCard
                                        id="meteo"
                                        active={selectedCategory === 'meteo'}
                                        label={language === 'fr' ? 'Météorologie' : 'Meteorology'}
                                        icon={
                                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                                            </svg>
                                        }
                                    />
                                </div>
                                <div className="flex-shrink-0 md:flex-shrink snap-center w-[140px] md:w-auto">
                                    <CategoryCard
                                        id="nav"
                                        active={selectedCategory === 'nav'}
                                        label={language === 'fr' ? 'Navigation' : 'Navigation'}
                                        icon={
                                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 01-.553-.894L15 4m0 13V4m0 0L9 7" />
                                            </svg>
                                        }
                                    />
                                </div>
                                <div className="flex-shrink-0 md:flex-shrink snap-center w-[140px] md:w-auto relative">
                                    <CategoryCard
                                        id="full"
                                        active={selectedCategory === 'full'}
                                        label={language === 'fr' ? 'Examen complet' : 'Full Exam'}
                                        icon={
                                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                            </svg>
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Filtered Exam List */}
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                            {!showCategories ? (
                                language === 'fr' ? 'Tous les examens' : 'All Exams'
                            ) : (
                                <>
                                    {selectedCategory === 'full' && (language === 'fr' ? 'Examens complets' : 'Full Exams')}
                                    {selectedCategory === 'law' && (language === 'fr' ? 'Droit aérien - Examens' : 'Air Law - Exams')}
                                    {selectedCategory === 'meteo' && (language === 'fr' ? 'Météorologie - Examens' : 'Meteorology - Exams')}
                                    {selectedCategory === 'nav' && (language === 'fr' ? 'Navigation - Examens' : 'Navigation - Exams')}
                                    {selectedCategory === 'gen' && (language === 'fr' ? 'Connaissance Générale - Examens' : 'General Knowledge - Exams')}
                                </>
                            )}
                        </h3>

                        <div className="space-y-4">
                            {filteredExams.length > 0 ? (
                                filteredExams.map((exam) => (
                                    <div
                                        key={exam.id}
                                        className="bg-white border border-gray-100 dark:bg-gray-900/50 dark:border-gray-800 rounded-xl p-6 flex flex-col md:flex-row items-center justify-between hover:border-blue-500/30 dark:hover:border-gray-700 transition group shadow-sm dark:shadow-none"
                                    >
                                        <div className="flex flex-col md:flex-row items-start md:items-center gap-6 w-full md:w-auto">
                                            <div className="flex-1">
                                                <div className="flex justify-between items-start w-full md:w-auto">
                                                    <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-1 group-hover:text-blue-600 dark:group-hover:text-white transition">
                                                        {language === 'fr' ? exam.title.fr : exam.title.en}
                                                    </h4>
                                                </div>

                                                {/* Meta Row */}
                                                <div className="flex items-center gap-6 mt-2 text-sm text-gray-500 dark:text-gray-500">
                                                    {exam.status === 'passed' && (
                                                        <span className="text-green-600 dark:text-green-400 font-medium flex items-center gap-1">
                                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                            {language === 'fr' ? 'Réussi' : 'Passed'}
                                                        </span>
                                                    )}
                                                    {exam.status === 'locked' && (
                                                        <span className="text-gray-400 dark:text-gray-600 font-medium flex items-center gap-1">
                                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                            </svg>
                                                            {language === 'fr' ? 'Verrouillé' : 'Locked'}
                                                        </span>
                                                    )}
                                                    <span>{exam.questionsCount} questions</span>
                                                    <span>{exam.duration} min</span>
                                                </div>

                                                {/* Progress Bar (Visual flair from mockup) */}
                                                <div className="mt-4 flex items-center gap-4 w-full md:min-w-[300px]">
                                                    {exam.bestScore ? (
                                                        <>
                                                            <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                                                                <div
                                                                    className="h-full bg-green-500 dark:bg-emerald-400 shadow-[0_0_10px_rgba(34,197,94,0.3)] dark:shadow-[0_0_10px_rgba(52,211,153,0.5)]"
                                                                    style={{ width: `${exam.bestScore}%` }}
                                                                />
                                                            </div>
                                                            <span className="text-gray-900 dark:text-white font-bold text-sm">{exam.bestScore} %</span>
                                                        </>
                                                    ) : (
                                                        exam.status !== 'locked' && (
                                                            <div className="text-gray-400 dark:text-gray-600 text-xs">
                                                                {language === 'fr' ? 'Pas encore commencé' : 'Not started yet'}
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <button className="mt-4 md:mt-0 px-6 py-2 bg-[#4c6ef5] hover:bg-[#405ec9] text-white rounded-full font-semibold transition opacity-0 group-hover:opacity-100 focus:opacity-100 shadow-lg shadow-blue-500/20 translate-x-4 group-hover:translate-x-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                            disabled={exam.status === 'locked'}
                                        >
                                            {language === 'fr' ? 'Commencer' : 'Start'}
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <div className="p-8 text-center border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-xl text-gray-500 dark:text-gray-400">
                                    {language === 'fr' ? 'Aucun examen trouvé dans cette catégorie.' : 'No exams found in this category.'}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'courses' && (
                <div className="py-20 text-center text-gray-500 dark:text-gray-400">
                    <p>{language === 'fr' ? 'Contenu des cours à venir...' : 'Course content coming soon...'}</p>
                </div>
            )}

            {activeTab === 'stats' && (
                <div className="py-20 text-center text-gray-500 dark:text-gray-400">
                    <p>{language === 'fr' ? 'Statistiques détaillées à venir...' : 'Detailed statistics coming soon...'}</p>
                </div>
            )}
        </div>
    );
};

export default TrainingHub;
